import { Busboy } from '@fastify/busboy';
import type { ToolsInput } from '@mastra/core/agent';
import type { Mastra } from '@mastra/core/mastra';
import type { RequestContext } from '@mastra/core/request-context';
import type { InMemoryTaskStore } from '@mastra/server/a2a/store';
import { findMatchingCustomRoute, isProtectedCustomRoute } from '@mastra/server/auth';
import type { MCPHttpTransportResult, MCPSseTransportResult } from '@mastra/server/handlers/mcp';
import type { ParsedRequestParams, ServerRoute } from '@mastra/server/server-adapter';
import {
  MastraServer as MastraServerBase,
  checkRouteFGA,
  getCustomHTTPExceptionResponse,
  isSafeObjectKey,
  isZodError,
  normalizeQueryParams,
  redactStreamChunk,
  serializeStreamChunk,
} from '@mastra/server/server-adapter';
import type { Application, NextFunction, Request, Response } from 'express';
export { createAuthMiddleware } from './auth-middleware';
export type { ExpressAuthMiddlewareOptions } from './auth-middleware';

type HasPermissionFn = (userPerms: string[], required: string) => boolean;
type AuthErrorWithHeaders = { status: number; error: string; headers?: Record<string, string> };
let _hasPermissionPromise: Promise<HasPermissionFn | undefined> | undefined;
function loadHasPermission(): Promise<HasPermissionFn | undefined> {
  if (!_hasPermissionPromise) {
    _hasPermissionPromise = import('@mastra/core/auth/ee')
      .then(m => m.hasPermission)
      .catch(() => {
        console.error(
          '[@mastra/express] Auth features require @mastra/core >= 1.6.0. Please upgrade: npm install @mastra/core@latest',
        );
        return undefined;
      });
  }
  return _hasPermissionPromise;
}

/**
 * Convert Express request to Web API Request for cookie-based auth providers.
 */
function toWebRequest(req: Request): globalThis.Request {
  const protocol = req.protocol || 'http';
  const host = req.get('host') || 'localhost';
  const url = `${protocol}://${host}${req.originalUrl || req.url}`;

  const headers = new Headers();
  for (const [key, value] of Object.entries(req.headers)) {
    if (value) {
      if (Array.isArray(value)) {
        value.forEach(v => headers.append(key, v));
      } else {
        headers.set(key, value);
      }
    }
  }

  return new globalThis.Request(url, {
    method: req.method,
    headers,
  });
}

// Extend Express types to include Mastra context
declare global {
  namespace Express {
    interface Locals {
      mastra: Mastra;
      requestContext: RequestContext;
      abortSignal: AbortSignal;
      registeredTools: ToolsInput;
      taskStore: InMemoryTaskStore;
      customRouteAuthConfig?: Map<string, boolean>;
    }
  }
}

export class MastraServer extends MastraServerBase<Application, Request, Response> {
  createContextMiddleware(): (req: Request, res: Response, next: NextFunction) => Promise<void> {
    return async (req: Request, res: Response, next: NextFunction) => {
      // Parse request context from request body and add to context
      let bodyRequestContext: Record<string, any> | undefined;
      let paramsRequestContext: Record<string, any> | undefined;

      // Parse request context from request body (POST/PUT)
      if (req.method === 'POST' || req.method === 'PUT') {
        const contentType = req.headers['content-type'];
        if (contentType?.includes('application/json') && req.body) {
          if (req.body.requestContext) {
            bodyRequestContext = req.body.requestContext;
          }
        }
      }

      // Parse request context from query params.
      if (req.method === 'GET' || req.method === 'POST') {
        try {
          const encodedRequestContext = req.query.requestContext;
          if (typeof encodedRequestContext === 'string') {
            // Try JSON first
            try {
              paramsRequestContext = JSON.parse(encodedRequestContext);
            } catch {
              // Fallback to base64(JSON)
              try {
                const json = Buffer.from(encodedRequestContext, 'base64').toString('utf-8');
                paramsRequestContext = JSON.parse(json);
              } catch {
                // ignore if still invalid
              }
            }
          }
        } catch {
          // ignore query parsing errors
        }
      }

      const requestContext = this.mergeRequestContext({ paramsRequestContext, bodyRequestContext });
      this.applyRequestMetadataToContext({
        requestContext,
        getHeader: name => req.get(name),
      });

      // Set context in res.locals
      res.locals.requestContext = requestContext;
      res.locals.mastra = this.mastra;
      res.locals.registeredTools = this.tools || {};
      if (this.taskStore) {
        res.locals.taskStore = this.taskStore;
      }
      res.locals.customRouteAuthConfig = this.customRouteAuthConfig;
      const controller = new AbortController();
      // Use res.on('close') instead of req.on('close') because the request's 'close' event
      // fires when the request body is fully consumed (e.g., after express.json() parses it),
      // NOT when the client disconnects. The response's 'close' event fires when the underlying
      // connection is actually closed, which is the correct signal for stream cleanup.
      res.on('close', () => {
        // Only abort if the response wasn't successfully completed
        if (!res.writableFinished) {
          controller.abort();
        }
      });
      res.locals.abortSignal = controller.signal;
      next();
    };
  }
  async stream(route: ServerRoute, res: Response, result: { fullStream: ReadableStream }): Promise<void> {
    const streamFormat = route.streamFormat || 'stream';

    if (streamFormat === 'sse') {
      res.setHeader('Content-Type', 'text/event-stream');
      res.setHeader('Cache-Control', 'no-cache');
      res.setHeader('Connection', 'keep-alive');
      res.setHeader('X-Accel-Buffering', 'no');
    } else {
      res.setHeader('Content-Type', 'text/plain');
    }
    res.setHeader('Transfer-Encoding', 'chunked');
    res.flushHeaders();

    if (streamFormat === 'sse' && route.sseFlushOnConnect) {
      res.write(': connected\n\n');
    }

    const readableStream = result instanceof ReadableStream ? result : result.fullStream;
    const reader = readableStream.getReader();

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (value) {
          if (streamFormat === 'sse' && typeof value === 'string' && value.startsWith(':')) {
            res.write(value);
            continue;
          }

          // Optionally redact sensitive data (system prompts, tool definitions, API keys) before sending to the client
          const shouldRedact = this.streamOptions?.redact ?? true;
          const outputValue = shouldRedact ? redactStreamChunk(value) : value;
          // A chunk that can't be serialized must not kill the stream — skip it and keep streaming
          const serialized = serializeStreamChunk(outputValue);
          if (!serialized.ok) {
            this.mastra.getLogger()?.error('Failed to serialize stream chunk, skipping', {
              path: route.path,
              chunkType: (outputValue as { type?: string })?.type,
              error: serialized.error.message,
            });
            continue;
          }
          if (streamFormat === 'sse') {
            res.write(`data: ${serialized.json}\n\n`);
          } else {
            res.write(serialized.json + '\x1E');
          }
        }
      }
    } catch (error) {
      this.mastra.getLogger()?.error('Error in stream processing', {
        error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
      });
    } finally {
      res.end();
    }
  }

  async getParams(route: ServerRoute, request: Request): Promise<ParsedRequestParams> {
    const urlParams = request.params as Record<string, string>;
    // Express's req.query can contain string | string[] | ParsedQs | ParsedQs[]
    const queryParams = normalizeQueryParams(request.query as Record<string, unknown>);
    let body: unknown;
    let bodyParseError: { message: string } | undefined;

    if (route.method === 'POST' || route.method === 'PUT' || route.method === 'PATCH' || route.method === 'DELETE') {
      const contentType = request.headers['content-type'] || '';

      if (contentType.includes('multipart/form-data')) {
        try {
          const maxFileSize = route.maxBodySize ?? this.bodyLimitOptions?.maxSize;
          body = await this.parseMultipartFormData(request, maxFileSize);
        } catch (error) {
          this.mastra.getLogger()?.error('Failed to parse multipart form data', {
            error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
          });
          // Re-throw size limit errors, let others fall through to validation
          if (error instanceof Error && error.message.toLowerCase().includes('size')) {
            throw error;
          }
          bodyParseError = {
            message: error instanceof Error ? error.message : 'Failed to parse multipart form data',
          };
        }
      } else {
        body = request.body;
      }
    }

    return { urlParams, queryParams, body, bodyParseError };
  }

  /**
   * Parse multipart/form-data using @fastify/busboy.
   * Converts file uploads to Buffers and parses JSON field values.
   *
   * @param request - The Express request object
   * @param maxFileSize - Optional maximum file size in bytes
   */
  private parseMultipartFormData(request: Request, maxFileSize?: number): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      const result: Record<string, unknown> = {};

      const busboy = new Busboy({
        headers: {
          'content-type': request.headers['content-type'] as string,
        },
        limits: maxFileSize ? { fileSize: maxFileSize } : undefined,
      });

      busboy.on('file', (fieldname: string, file: NodeJS.ReadableStream) => {
        const chunks: Buffer[] = [];
        let limitExceeded = false;

        file.on('data', (chunk: Buffer) => {
          chunks.push(chunk);
        });

        file.on('limit', () => {
          limitExceeded = true;
          reject(new Error(`File size limit exceeded${maxFileSize ? ` (max: ${maxFileSize} bytes)` : ''}`));
        });

        file.on('end', () => {
          if (!limitExceeded && isSafeObjectKey(fieldname)) {
            result[fieldname] = Buffer.concat(chunks);
          }
        });
      });

      busboy.on('field', (fieldname: string, value: string) => {
        if (!isSafeObjectKey(fieldname)) {
          return;
        }
        // Try to parse JSON strings (like 'options')
        try {
          result[fieldname] = JSON.parse(value);
        } catch {
          result[fieldname] = value;
        }
      });

      busboy.on('finish', () => {
        resolve(result);
      });

      busboy.on('error', (error: Error) => {
        reject(error);
      });

      request.pipe(busboy);
    });
  }

  async sendResponse(
    route: ServerRoute,
    response: Response,
    result: unknown,
    request?: Request,
    prefix?: string,
  ): Promise<void> {
    const resolvedPrefix = prefix ?? this.prefix ?? '';

    // Apply refresh headers from transparent session refresh (e.g. Set-Cookie after token refresh)
    if (result && typeof result === 'object' && '__refreshHeaders' in result) {
      const refreshHeaders = (result as any).__refreshHeaders as Record<string, string>;
      for (const [key, value] of Object.entries(refreshHeaders)) {
        response.setHeader(key, value);
      }
      delete (result as any).__refreshHeaders;
    }

    if (route.responseType === 'json') {
      response.json(result);
    } else if (route.responseType === 'stream') {
      await this.stream(route, response, result as { fullStream: ReadableStream });
    } else if (route.responseType === 'datastream-response') {
      // Handle AI SDK Response objects - pipe Response.body to Express response
      const fetchResponse = result as globalThis.Response;
      fetchResponse.headers.forEach((value, key) => response.setHeader(key, value));
      response.status(fetchResponse.status);
      if (fetchResponse.body) {
        const reader = fetchResponse.body.getReader();

        const onResError = (err: unknown) => {
          this.mastra.getLogger()?.error('Error writing datastream response', {
            error: err instanceof Error ? { message: err.message, stack: err.stack } : err,
          });
          void reader.cancel('response write error').catch(() => {});
        };
        response.once('error', onResError);

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            response.write(value);
          }
        } catch (error) {
          this.mastra.getLogger()?.error('Error in datastream processing', {
            error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
          });
        } finally {
          response.off('error', onResError);
          response.end();
        }
      } else {
        response.end();
      }
    } else if (route.responseType === 'mcp-http') {
      // MCP Streamable HTTP transport - request is required
      if (!request) {
        response.status(500).json({ error: 'Request object required for MCP transport' });
        return;
      }

      const { server, httpPath, mcpOptions: routeMcpOptions } = result as MCPHttpTransportResult;

      try {
        // Merge class-level mcpOptions with route-specific options (route takes precedence)
        const options = { ...this.mcpOptions, ...routeMcpOptions };

        await server.startHTTP({
          url: new URL(request.url, `http://${request.headers.host}`),
          httpPath: `${resolvedPrefix}${httpPath}`,
          req: request,
          res: response,
          options: Object.keys(options).length > 0 ? options : undefined,
        });
        // Response handled by startHTTP
      } catch {
        if (!response.headersSent) {
          response.status(500).json({
            jsonrpc: '2.0',
            error: { code: -32603, message: 'Internal server error' },
            id: null,
          });
        }
      }
    } else if (route.responseType === 'mcp-sse') {
      // MCP SSE transport - request is required
      if (!request) {
        response.status(500).json({ error: 'Request object required for MCP transport' });
        return;
      }

      const { server, ssePath, messagePath } = result as MCPSseTransportResult;

      try {
        await server.startSSE({
          url: new URL(request.url, `http://${request.headers.host}`),
          ssePath: `${resolvedPrefix}${ssePath}`,
          messagePath: `${resolvedPrefix}${messagePath}`,
          req: request,
          res: response,
        });
        // Response handled by startSSE
      } catch {
        if (!response.headersSent) {
          response.status(500).json({ error: 'Error handling MCP SSE request' });
        }
      }
    } else {
      response.sendStatus(500);
    }
  }

  async registerRoute(
    app: Application,
    route: ServerRoute,
    { prefix: prefixParam }: { prefix?: string } = {},
  ): Promise<void> {
    // Default prefix to this.prefix if not provided, or empty string
    const prefix = prefixParam ?? this.prefix ?? '';

    const maxSize = route.maxBodySize ?? this.bodyLimitOptions?.maxSize;
    const isBodyMethod = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(route.method.toUpperCase());

    // Create middleware array
    const middlewares: Array<(req: Request, res: Response, next: NextFunction) => void> = [];

    // Add body limit middleware if needed
    if (isBodyMethod && maxSize !== undefined) {
      const bodyLimitMiddleware = (req: Request, res: Response, next: NextFunction) => {
        const contentLength = req.headers['content-length'];
        // A host-level parser may run before this route middleware, so this is a
        // post-parse safeguard when Content-Length is unavailable.
        const parsedLength =
          contentLength === undefined && req.body !== undefined
            ? Buffer.byteLength(JSON.stringify(req.body), 'utf8')
            : 0;
        if ((contentLength && parseInt(contentLength, 10) > maxSize) || parsedLength > maxSize) {
          let errorResponse: unknown = { error: 'Request body too large' };
          if (route.maxBodySize === undefined && this.bodyLimitOptions) {
            try {
              errorResponse = this.bodyLimitOptions.onError(errorResponse);
            } catch {
              // Fall back to the default response.
            }
          }
          return res.status(413).json(errorResponse);
        }
        next();
      };
      middlewares.push(bodyLimitMiddleware);
    }

    app[route.method.toLowerCase() as keyof Application](
      `${prefix}${route.path}`,
      ...middlewares,
      async (req: Request, res: Response) => {
        // Check route-level authentication/authorization
        const authError = await this.checkRouteAuth(route, {
          path: String(req.path || '/'),
          method: String(req.method || 'GET'),
          getHeader: name => req.headers[name.toLowerCase()] as string | undefined,
          getQuery: name => req.query[name] as string | undefined,
          requestContext: res.locals.requestContext,
          request: toWebRequest(req),
          buildAuthorizeContext: () => toWebRequest(req),
        });

        if (authError) {
          const authResult = authError as AuthErrorWithHeaders;
          // Apply any refresh headers (e.g. Set-Cookie from transparent session refresh)
          if (authResult.headers) {
            for (const [key, value] of Object.entries(authResult.headers)) {
              res.setHeader(key, value);
            }
          }

          // If this is an auth error (not just a success-with-headers), return error response
          if (authResult.error) {
            return res.status(authResult.status).json({ error: authResult.error });
          }
        }

        const params = await this.getParams(route, req);

        // Return 400 Bad Request if body parsing failed (e.g., malformed multipart data)
        if (params.bodyParseError) {
          return res.status(400).json({
            error: 'Invalid request body',
            issues: [{ field: 'body', message: params.bodyParseError.message }],
          });
        }

        if (params.queryParams) {
          try {
            params.queryParams = await this.parseQueryParams(route, params.queryParams);
          } catch (error) {
            this.mastra.getLogger()?.error('Error parsing query params', {
              error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
            });
            if (isZodError(error)) {
              const { status, body } = this.resolveValidationError(route, error, 'query');
              return res.status(status).json(body);
            }
            return res.status(400).json({
              error: 'Invalid query parameters',
              issues: [{ field: 'unknown', message: error instanceof Error ? error.message : 'Unknown error' }],
            });
          }
        }

        if (params.body !== undefined || route.bodySchema) {
          try {
            params.body = await this.parseBody(route, params.body);
          } catch (error) {
            this.mastra.getLogger()?.error('Error parsing body', {
              error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
            });
            if (isZodError(error)) {
              const { status, body } = this.resolveValidationError(route, error, 'body');
              return res.status(status).json(body);
            }
            return res.status(400).json({
              error: 'Invalid request body',
              issues: [{ field: 'unknown', message: error instanceof Error ? error.message : 'Unknown error' }],
            });
          }
        }

        // Parse path params through pathParamSchema for type coercion (e.g., z.coerce.number())
        if (params.urlParams) {
          try {
            params.urlParams = await this.parsePathParams(route, params.urlParams);
          } catch (error) {
            this.mastra.getLogger()?.error('Error parsing path params', {
              error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
            });
            if (isZodError(error)) {
              const { status, body } = this.resolveValidationError(route, error, 'path');
              return res.status(status).json(body);
            }
            return res.status(400).json({
              error: 'Invalid path parameters',
              issues: [{ field: 'unknown', message: error instanceof Error ? error.message : 'Unknown error' }],
            });
          }
        }

        const handlerParams = {
          ...params.urlParams,
          ...params.queryParams,
          ...(typeof params.body === 'object' ? params.body : {}),
          requestContext: res.locals.requestContext,
          mastra: this.mastra,
          registeredTools: res.locals.registeredTools,
          taskStore: res.locals.taskStore,
          abortSignal: res.locals.abortSignal,
          routePrefix: prefix,
          request: toWebRequest(req),
        };

        // Check route permission requirement (EE feature)
        // Uses convention-based permission derivation: permissions are auto-derived
        // from route path/method unless explicitly set or route is public
        const authConfig = this.mastra.getServer()?.auth;
        if (authConfig) {
          const hasPermission = await loadHasPermission();
          if (hasPermission) {
            const userPermissions = res.locals.requestContext.get('mastra__userPermissions') as string[] | undefined;
            const permissionError = this.checkRoutePermission(route, userPermissions, hasPermission);

            if (permissionError) {
              return res.status(permissionError.status).json({
                error: permissionError.error,
                message: permissionError.message,
              });
            }
          }
        }

        // Check FGA authorization (EE feature)
        const fgaError = await checkRouteFGA(this.mastra, route, res.locals.requestContext, {
          ...params.urlParams,
          ...params.queryParams,
          ...(typeof params.body === 'object' ? params.body : {}),
        });
        if (fgaError) {
          return res.status(fgaError.status).json({ error: fgaError.error, message: fgaError.message });
        }

        try {
          const result = await route.handler(handlerParams);
          await this.sendResponse(route, res, result, req, prefix);
        } catch (error) {
          const httpStatus =
            error && typeof error === 'object' ? ((error as any).status ?? (error as any).details?.status) : undefined;
          const isClientError = typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500;
          if (!isClientError) {
            // 501 means an optional capability isn't provided by the configured storage or core: expected, not a server fault.
            const logLevel = httpStatus === 501 ? 'warn' : 'error';
            this.mastra.getLogger()?.[logLevel]('Error calling handler', {
              error: error instanceof Error ? { message: error.message, stack: error.stack } : error,
              path: route.path,
              method: route.method,
            });
          }
          const customResponse = getCustomHTTPExceptionResponse(error);
          if (customResponse) {
            customResponse.headers.forEach((value, name) => res.setHeader(name, value));
            res.status(customResponse.status).end(Buffer.from(await customResponse.arrayBuffer()));
            return;
          }

          // Check if it's an HTTPException or MastraError with a status code
          let status = 500;
          if (error && typeof error === 'object') {
            // Check for direct status property (HTTPException)
            if ('status' in error) {
              status = (error as any).status;
            }
            // Check for MastraError with status in details
            else if (
              'details' in error &&
              error.details &&
              typeof error.details === 'object' &&
              'status' in error.details
            ) {
              status = (error.details as any).status;
            }
          }
          res.status(status).json({ error: error instanceof Error ? error.message : 'Unknown error' });
        }
      },
    );
  }

  async registerCustomApiRoutes(): Promise<void> {
    const routes = await this.registerSchemaApiRoutes();
    if (!(await this.buildCustomRouteHandler(routes))) return;

    this.app.use(async (req: Request, res: Response, next: NextFunction) => {
      // Check if this request matches a protected custom route and run auth
      const path = String(req.path || '/');
      const method = String(req.method || 'GET');
      const matchedRoute = findMatchingCustomRoute(
        path,
        method,
        this.customApiRoutes ?? this.mastra.getServer()?.apiRoutes,
      );
      const shouldRunCustomRouteAuth = isProtectedCustomRoute(path, method, this.customRouteAuthConfig);
      const shouldRunCustomRouteFGA = !!matchedRoute?.route.fga;

      if (shouldRunCustomRouteAuth || shouldRunCustomRouteFGA) {
        const serverRoute: ServerRoute = {
          method: (matchedRoute?.route.method ?? method) as any,
          path: matchedRoute?.route.path ?? path,
          responseType: 'json',
          handler: async () => {},
          requiresAuth: matchedRoute?.route.requiresAuth,
          requiresPermission: matchedRoute?.route.requiresPermission,
          fga: matchedRoute?.route.fga,
        };

        if (shouldRunCustomRouteAuth) {
          const authError = await this.checkRouteAuth(serverRoute, {
            path,
            method,
            getHeader: name => req.headers[name.toLowerCase()] as string | undefined,
            getQuery: name => req.query[name] as string | undefined,
            requestContext: res.locals.requestContext,
            request: toWebRequest(req),
            buildAuthorizeContext: () => toWebRequest(req),
          });

          if (authError) {
            const authResult = authError as AuthErrorWithHeaders;
            if (authResult.headers) {
              for (const [key, value] of Object.entries(authResult.headers)) {
                res.setHeader(key, value);
              }
            }
            if (authResult.error) {
              return res.status(authResult.status).json({ error: authResult.error });
            }
          }

          const authConfig = this.mastra.getServer()?.auth;
          if (authConfig) {
            const hasPermission = await loadHasPermission();
            if (hasPermission) {
              const userPermissions = res.locals.requestContext.get('mastra__userPermissions') as string[] | undefined;
              const permissionError = this.checkRoutePermission(serverRoute, userPermissions, hasPermission);
              if (permissionError) {
                return res.status(permissionError.status).json({
                  error: permissionError.error,
                  message: permissionError.message,
                });
              }
            }
          }
        }

        // Check FGA authorization (EE feature)
        const fgaError = await checkRouteFGA(this.mastra, serverRoute, res.locals.requestContext, {
          ...(matchedRoute?.params ?? {}),
          ...(req.query as Record<string, string>),
          ...(typeof req.body === 'object' && req.body !== null ? req.body : {}),
        });
        if (fgaError) {
          return res.status(fgaError.status).json({ error: fgaError.error, message: fgaError.message });
        }
      }

      const response = await this.handleCustomRouteRequest(
        `${req.protocol}://${req.get('host') || 'localhost'}${req.originalUrl}`,
        req.method,
        req.headers as Record<string, string | string[] | undefined>,
        req.body,
        res.locals.requestContext,
        res.locals.abortSignal,
      );
      if (!response) return next();
      await this.writeCustomRouteResponse(response, res, res.locals.abortSignal);
    });
  }

  registerContextMiddleware(): void {
    this.app.use(this.createContextMiddleware());
    this.app.use((req, res, next) => {
      const path = String(req.path || '/');
      const method = String(req.method || 'GET');
      res.on('finish', () => {
        this.warnIfUnregisteredChannelWebhook(path, method, res.statusCode);
      });
      next();
    });
  }

  registerAuthMiddleware(): void {
    // Auth is handled per-route in registerRoute() and registerCustomApiRoutes()
    // No global middleware needed
  }

  registerHttpLoggingMiddleware(): void {
    if (!this.httpLoggingConfig?.enabled) {
      return;
    }

    this.app.use((req, res, next) => {
      if (!this.shouldLogRequest(req.path)) {
        return next();
      }

      const start = Date.now();
      const method = req.method;
      const path = req.path;

      res.on('finish', () => {
        const duration = Date.now() - start;
        const status = res.statusCode;
        const level = this.httpLoggingConfig?.level || 'info';

        const logData: Record<string, any> = {
          method,
          path,
          status,
          duration: `${duration}ms`,
        };

        if (this.httpLoggingConfig?.includeQueryParams) {
          logData.query = req.query;
        }

        if (this.httpLoggingConfig?.includeHeaders) {
          const headers = { ...req.headers };
          const redactHeaders = this.httpLoggingConfig.redactHeaders || [];
          redactHeaders.forEach(h => {
            const key = h.toLowerCase();
            if (headers[key] !== undefined) {
              headers[key] = '[REDACTED]';
            }
          });
          logData.headers = headers;
        }

        this.logger[level](`${method} ${path} ${status} ${duration}ms`, logData);
      });

      next();
    });
  }
}
